import { getCombinedRect, overlapPercentage } from 'widgets/WidgetLayout.helpers';

import { Cluster, DivisionDirection } from './kMeans.types';
import { WidgetSegmentGroupMember } from './segmentAndGroupWidgets.types';

// can be tuned up for better accuracy
const ACCURACY = 100;
// convergence = we are done if the centroid positions have moved less than 0.001 from the previous iteration
const CONVERGENCE_CUTOFF = 0.001;

/**
 * Divides an array of WidgetSegmentGroupMembers into k groups using the k-means algorithm.
 * Either vertically or horizontally.
 *
 * @param rects - Array of rectangles to divide (WidgetSegmentGroupMembers).
 * @param k - The number of groups to divide the boxes into.
 * @param direction - The direction in which to divide the boxes ('vertical' or 'horizontal').
 *
 * @returns An object containing the divided groups and the division points.
 */
export function kMeans(rects: WidgetSegmentGroupMember[], k: number, direction: DivisionDirection) {
  // need at least 2 rects to divide
  if (k < 2) return { groups: [], divisionPoints: [] };

  // Determine which coordinates to use based on direction
  const getCoordinate = (rect: WidgetSegmentGroupMember) =>
    direction === 'vertical'
      ? // X coordinate of center - group rects side-by-side (left-to-right)
        rect.rect.leftPx + rect.rect.widthPx / 2
      : // Y coordinate of center - group rects top-to-bottom
        rect.rect.topPx + rect.rect.heightPx / 2;

  const points: number[] = rects.map(getCoordinate);

  // Initialize the centroids from k - a centroid is a single number representing a position along that axis
  // start with evenly spaced centroids between the min and max x|y-coordinates
  const centroids: number[] = [];
  for (let i = 0; i < k; i++) {
    centroids.push(Math.min(...points) + ((Math.max(...points) - Math.min(...points)) * i) / (k - 1));
  }

  // find the centroid positions that best divide the rects into k groups
  for (let iteration = 0; iteration < ACCURACY; iteration++) {
    // add empty clusters - one for each k
    const clusters: Cluster[] = Array(k)
      .fill(null)
      .map(() => []);

    // iterate over the x|y points - find the closest centroid for each point
    points.forEach((point, index) => {
      // distance of the point to each centroid
      // 1d space so we can use the absolute distance
      const distances = centroids.map((centroid) => Math.abs(point - centroid));
      const closestCentroidIndex = distances.indexOf(Math.min(...distances));

      // add the point to the cluster of the closest centroid
      clusters[closestCentroidIndex].push(index);
    });

    // Recalculate the centroid positions based on the assigned points
    // the new centroid position is the average of the points in the cluster
    const newCentroids = clusters.map((cluster) => {
      if (cluster.length > 0) {
        const pointsTotal = cluster.reduce((sum, index) => sum + points[index], 0);
        return pointsTotal / cluster.length;
      }

      return null;
    });

    // If a centroid is null (no points assigned), assign it to the previous value (TODO: hmm maybe keep it as null?)
    const formattedNewCentroids = newCentroids.map((centroid, i) => (centroid === null ? centroids[i] : centroid));

    // Check for convergence
    // We are done if the centroid positions have moved less than 0.001 from the previous iteration
    if (formattedNewCentroids.every((centroid, i) => Math.abs(centroid - centroids[i]) < CONVERGENCE_CUTOFF)) {
      break;
    }

    // update the centroid positions with the new ones
    formattedNewCentroids.forEach((centroid, i) => (centroids[i] = centroid));
  }

  // turn it into groups of WidgetSegmentGroupMembers - one group for each centroid position (k)
  const groups: WidgetSegmentGroupMember[][] = Array(k)
    .fill(null)
    .map(() => []);

  // assign each box to the closest centroid
  rects.forEach((rect) => {
    const center = getCoordinate(rect);
    const distances = centroids.map((centroid) => Math.abs(center - centroid));
    const closestCentroidIndex = distances.indexOf(Math.min(...distances));

    groups[closestCentroidIndex].push(rect);
  });

  return { groups, divisionPoints: centroids };
}

const VALID_HORIZONTAL_SPLIT = {
  maxOverlap: 35,
  minHeight: 200,
};

/**
 * Checks if the horizontal split of widget segment groups is valid.
 *
 * @param groups - An array of widget segment groups.
 * @returns A boolean indicating whether the horizontal split is valid.
 */
export const isValidHorizontalSplit = (groups: WidgetSegmentGroupMember[][]) => {
  const groupRects = groups.map((g) => getCombinedRect(g.map((m) => m.rect)));

  for (let i = 0; i < groups.length; i++) {
    const groupRect = groupRects[i];
    const height = groupRect.heightPx;

    // the height does not meet the minimum height requirement
    if (height < VALID_HORIZONTAL_SPLIT.minHeight) {
      return false;
    }

    // check if its within the allowed overlap - if a group overlaps more than the allowed percentage with another group, it's not valid
    for (let j = 0; j < groups.length; j++) {
      if (i === j) continue;

      const overlap = overlapPercentage(groupRect, groupRects[j]);
      if (overlap > VALID_HORIZONTAL_SPLIT.maxOverlap) {
        return false;
      }
    }
  }

  return true;
};

const VALID_VERTICAL_SPLIT = {
  maxOverlap: 35,
  minWidth: 200,
  minHeight: 50,
};

/**
 * Checks if the vertical split of widget segment groups is valid.
 *
 * @param groups - An array of widget segment groups.
 * @returns A boolean indicating whether the horizontal split is valid.
 */
export const isValidVerticalSplit = (groups: WidgetSegmentGroupMember[][]) => {
  const groupRects = groups.map((g) => getCombinedRect(g.map((m) => m.rect)));

  for (let i = 0; i < groups.length; i++) {
    const groupRect = groupRects[i];
    const width = groupRect.widthPx;
    const height = groupRect.heightPx;

    // the width does not meet the minimum width requirement
    if (width < VALID_VERTICAL_SPLIT.minWidth) {
      return false;
    }

    if (height < VALID_VERTICAL_SPLIT.minHeight) {
      return false;
    }

    // check if its within the allowed overlap
    for (let j = 0; j < groups.length; j++) {
      if (i === j) continue;

      const overlap = overlapPercentage(groupRect, groupRects[j]);
      if (overlap > VALID_VERTICAL_SPLIT.maxOverlap) {
        return false;
      }
    }
  }

  return true;
};
